<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import integrationsAPI from 'dashboard/api/integrations';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const store = useStore();

const configuration = ref({ configured: false, platforms: [] });
const selectedFile = ref(null);
const selectedRoleKeys = ref(['administrator']);
const rolesDirty = ref(false);
const fileInput = ref(null);
const removeDialog = ref(null);
const isLoading = ref(true);
const isSaving = ref(false);
const isRemoving = ref(false);
const logoUrl = '/dashboard/images/integrations/whchat-v2.svg';

const statusLabel = computed(() =>
  configuration.value.configured
    ? t('INTEGRATION_APPS.WHCHAT_V2.STATUS.CONFIGURED')
    : t('INTEGRATION_APPS.WHCHAT_V2.STATUS.NOT_CONFIGURED')
);

const canSave = computed(
  () =>
    selectedFile.value || (configuration.value.configured && rolesDirty.value)
);

const onFileSelected = event => {
  [selectedFile.value] = event.target.files;
};

const resetFileInput = () => {
  selectedFile.value = null;
  if (fileInput.value) fileInput.value.value = '';
};

const loadConfiguration = async () => {
  isLoading.value = true;
  try {
    const { data } = await integrationsAPI.getWhchatV2Configuration();
    configuration.value = data;
    selectedRoleKeys.value = data.allowed_role_keys || ['administrator'];
    rolesDirty.value = false;
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INTEGRATION_APPS.WHCHAT_V2.API.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const uploadConfiguration = async () => {
  if (!canSave.value) return;

  isSaving.value = true;
  const payload = new FormData();
  if (selectedFile.value) {
    payload.append('firebase_admin_file', selectedFile.value);
  }
  selectedRoleKeys.value.forEach(roleKey => {
    payload.append('allowed_role_keys[]', roleKey);
  });

  try {
    const { data } = await integrationsAPI.updateWhchatV2Configuration(payload);
    configuration.value = data;
    selectedRoleKeys.value = data.allowed_role_keys || ['administrator'];
    rolesDirty.value = false;
    resetFileInput();
    await store.dispatch('integrations/get');
    useAlert(t('INTEGRATION_APPS.WHCHAT_V2.API.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INTEGRATION_APPS.WHCHAT_V2.API.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const removeConfiguration = async () => {
  isRemoving.value = true;
  try {
    await integrationsAPI.deleteWhchatV2Configuration();
    configuration.value = {
      ...configuration.value,
      configured: false,
      project_id: null,
      client_email: null,
      original_filename: null,
      allowed_role_keys: ['administrator'],
    };
    selectedRoleKeys.value = ['administrator'];
    rolesDirty.value = false;
    resetFileInput();
    await store.dispatch('integrations/get');
    useAlert(t('INTEGRATION_APPS.WHCHAT_V2.API.REMOVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INTEGRATION_APPS.WHCHAT_V2.API.REMOVE_ERROR')
    );
  } finally {
    isRemoving.value = false;
    removeDialog.value?.close();
  }
};

onMounted(loadConfiguration);
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_APPS.WHCHAT_V2.HEADER')"
        :description="$t('INTEGRATION_APPS.WHCHAT_V2.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>

    <template #body>
      <div class="flex flex-col max-w-3xl gap-5">
        <section
          class="flex flex-col gap-5 p-5 border rounded-xl border-n-weak bg-n-card"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="flex items-start gap-4">
              <img
                :src="logoUrl"
                class="size-14 rounded-xl border border-n-weak shadow-sm"
                alt="WH Chat"
              />
              <div>
                <h2 class="mb-1 text-heading-2 text-n-slate-12">
                  {{ $t('INTEGRATION_APPS.WHCHAT_V2.CREDENTIAL_TITLE') }}
                </h2>
                <p class="mb-0 text-sm text-n-slate-10">
                  {{ $t('INTEGRATION_APPS.WHCHAT_V2.ONE_FILE_HELP') }}
                </p>
              </div>
            </div>
            <Label
              :label="statusLabel"
              :color="configuration.configured ? 'teal' : 'slate'"
              compact
            />
          </div>

          <div
            v-if="configuration.configured"
            class="grid gap-3 p-4 text-sm border rounded-xl sm:grid-cols-2 border-n-weak bg-n-alpha-2"
          >
            <div>
              <span class="block text-n-slate-10">
                {{ $t('INTEGRATION_APPS.WHCHAT_V2.PROJECT_ID') }}
              </span>
              <span class="font-medium break-all text-n-slate-12">
                {{ configuration.project_id }}
              </span>
            </div>
            <div>
              <span class="block text-n-slate-10">
                {{ $t('INTEGRATION_APPS.WHCHAT_V2.CLIENT_EMAIL') }}
              </span>
              <span class="font-medium break-all text-n-slate-12">
                {{ configuration.client_email }}
              </span>
            </div>
            <div>
              <span class="block text-n-slate-10">
                {{ $t('INTEGRATION_APPS.WHCHAT_V2.FILE') }}
              </span>
              <span class="font-medium break-all text-n-slate-12">
                {{ configuration.original_filename }}
              </span>
            </div>
            <div>
              <span class="block text-n-slate-10">
                {{ $t('INTEGRATION_APPS.WHCHAT_V2.API_PATH') }}
              </span>
              <code class="font-medium text-n-slate-12">
                {{ configuration.api_path }}
              </code>
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <div>
              <h3 class="mb-1 text-heading-3 text-n-slate-12">
                {{ $t('INTEGRATION_APPS.WHCHAT_V2.ROLES_TITLE') }}
              </h3>
              <p class="mb-0 text-sm text-n-slate-10">
                {{ $t('INTEGRATION_APPS.WHCHAT_V2.ROLES_HELP') }}
              </p>
            </div>
            <div class="grid gap-3 sm:grid-cols-2">
              <label
                v-for="role in configuration.available_roles"
                :key="role.key"
                class="flex items-start gap-3 p-3 border rounded-xl border-n-weak"
                :class="
                  role.locked
                    ? 'cursor-not-allowed bg-n-alpha-2'
                    : 'cursor-pointer'
                "
              >
                <input
                  v-model="selectedRoleKeys"
                  type="checkbox"
                  :value="role.key"
                  :disabled="role.locked"
                  class="mt-0.5 size-4 accent-n-brand"
                  @change="rolesDirty = true"
                />
                <span>
                  <span class="block font-medium text-n-slate-12">
                    {{ role.name }}
                  </span>
                  <span class="block text-xs text-n-slate-10">
                    {{
                      role.locked
                        ? $t('INTEGRATION_APPS.WHCHAT_V2.ROLE_REQUIRED')
                        : role.type === 'custom_role'
                          ? $t('INTEGRATION_APPS.WHCHAT_V2.CUSTOM_ROLE')
                          : $t('INTEGRATION_APPS.WHCHAT_V2.STANDARD_ROLE')
                    }}
                  </span>
                </span>
              </label>
            </div>
          </div>

          <label class="flex flex-col gap-2">
            <span class="text-heading-3 text-n-slate-12">
              {{
                configuration.configured
                  ? $t('INTEGRATION_APPS.WHCHAT_V2.REPLACE_FILE')
                  : $t('INTEGRATION_APPS.WHCHAT_V2.SELECT_FILE')
              }}
            </span>
            <input
              ref="fileInput"
              type="file"
              accept=".json,application/json"
              class="block w-full text-sm rounded-lg cursor-pointer text-n-slate-11 file:mr-3 file:border-0 file:px-3 file:py-2 file:bg-n-alpha-3 file:text-n-slate-12"
              @change="onFileSelected"
            />
            <span class="text-sm text-n-slate-10">
              {{ $t('INTEGRATION_APPS.WHCHAT_V2.FILE_HELP') }}
            </span>
          </label>

          <div
            class="flex gap-3 p-4 text-sm border rounded-xl border-n-weak bg-n-alpha-2 text-n-slate-11"
          >
            <span
              class="i-lucide-shield-check mt-0.5 size-5 shrink-0 text-n-teal-10"
            />
            <div>
              <p class="mb-1 font-medium text-n-slate-12">
                {{ $t('INTEGRATION_APPS.WHCHAT_V2.SECURITY_TITLE') }}
              </p>
              <p class="mb-0">
                {{ $t('INTEGRATION_APPS.WHCHAT_V2.SECURITY_HELP') }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap justify-end gap-3">
            <Button
              v-if="configuration.configured"
              :label="$t('INTEGRATION_APPS.WHCHAT_V2.REMOVE')"
              ruby
              faded
              :disabled="isSaving || isRemoving"
              @click="removeDialog?.open()"
            />
            <Button
              :label="$t('INTEGRATION_APPS.WHCHAT_V2.SAVE')"
              teal
              :is-loading="isSaving"
              :disabled="!canSave || isRemoving"
              @click="uploadConfiguration"
            />
          </div>
        </section>

        <section
          class="p-5 text-sm border rounded-xl border-n-weak bg-n-card text-n-slate-11"
        >
          <h3 class="mb-2 text-heading-3 text-n-slate-12">
            {{ $t('INTEGRATION_APPS.WHCHAT_V2.MOBILE_TITLE') }}
          </h3>
          <p class="mb-0">
            {{ $t('INTEGRATION_APPS.WHCHAT_V2.MOBILE_HELP') }}
          </p>
        </section>
      </div>

      <Dialog
        ref="removeDialog"
        type="alert"
        :title="$t('INTEGRATION_APPS.WHCHAT_V2.REMOVE_CONFIRM_TITLE')"
        :description="$t('INTEGRATION_APPS.WHCHAT_V2.REMOVE_CONFIRM_HELP')"
        :confirm-button-label="$t('INTEGRATION_APPS.WHCHAT_V2.REMOVE')"
        :cancel-button-label="$t('INTEGRATION_APPS.WHCHAT_V2.CANCEL')"
        :is-loading="isRemoving"
        @confirm="removeConfiguration"
      />
    </template>
  </SettingsLayout>
</template>
